import React, { useState, useEffect } from "react";

import isEmpty from "lodash/isEmpty";
import { useDispatch } from "react-redux";

import { removeFormErrors } from "store/form/actions";

import {
    getHeatingSystemTemplate,
    getEStarOptions,
    getEStarManufacturerOptions,
    getEStarModelOptions,
    getDefaultFurnaceSpecs,
    getDefaultBoilerSpecs,
} from "utils/mechanicals";
import { useStateSelector } from "utils/field";
import { getValidation } from "utils/fieldValidation";

import Field from "components/Field";

import InputRow from "components/Input/Row";
import Select from "components/Input/Select";
import Button from "components/Button";
import Baseboards from "./Baseboards";
import FurnaceBoiler from "./FurnaceBoiler";
import Combo from "./Combo";
import P9Combo from "./P9Combo";
import FansAndPumps from "./FansAndPumps";
import Drawer from "components/Drawer";
import AddSystem from "./AddSystem";
import RadiantHeating from "./RadiantHeating";
import AdditionalOpenings from "./AdditionalOpenings";
import SupplementaryHeating from "./SupplementaryHeating";
import EmptyState from "components/EmptyState";
import Accordion from "components/Accordion";
import AddIcon from "assets/images/icons/JSX/Add";

import classes from "../style.module.scss";
import sharedClasses from "features/Model/sharedStyles.module.scss";

const primaryHeatingValidation = getValidation("heatingPrimarySelected");

const typeContent = (type) =>
    ({
        baseboards: {
            name: "Baseboard, Hydronic, or Plenum Heater",
            Component: Baseboards,
        },
        furnace: {
            name: "Furnace",
            Component: FurnaceBoiler,
        },
        boiler: {
            name: "Boiler",
            Component: FurnaceBoiler,
        },
        combo: {
            name: "Combo Heating/DHW",
            Component: Combo,
        },
        p9Combo: {
            name: "Combo Heating/DHW (CSA P.9-11 Tested)",
            Component: P9Combo,
        },
        default: {
            name: "",
            Component: () => <></>,
        },
    })[type || "default"];

export default ({
    accessor,
    favId,
    change,
    isUpgrade = false,
    formName,
    hidePrimaryHeating = false,
    hideSecondaryHeating = false,
    canBeEditedFromParametric = true,
    ...props
}) => {
    const dispatch = useDispatch();
    const [open, toggleOpen] = useState(false);

    const basementComponents = useStateSelector(`model.modelData.components.basement`) || {};
    const needsHeatingReview = Boolean(useStateSelector("model.modelData.uiSettings.completion.needsHeatingReview"));

    useEffect(() => {
        if (isUpgrade || accessor !== "model.modelData.heatingCooling" || !needsHeatingReview || !change) {
            return;
        }
        change("model.modelData.uiSettings.completion.needsHeatingReview", false);
    }, [accessor, change, isUpgrade, needsHeatingReview]);

    const currentPrimarySystem = Object.keys(useStateSelector(`${accessor}.heating.system`) || {})[0];
    const primarySystemType = useStateSelector(`${accessor}.heating.selected.type`);
    const eStarManufacturerValue = useStateSelector(`${accessor}.heating.selected.eStar.manufacturer`);
    const eStarModelId = useStateSelector(`${accessor}.heating.selected.eStar.modelId`);

    const radiantSystem = useStateSelector(`${accessor}.radiantHeating`) || {};
    const additionalOpenings = useStateSelector(`${accessor}.additionalOpenings`) || {};

    const supplSystems = useStateSelector(`${accessor}.supplSystems.systems`) || {};

    let noBasementComponents = false;
    if (isEmpty(basementComponents)) {
        noBasementComponents = true;
    } else {
        noBasementComponents = Object.keys(basementComponents)
            .reduce((acc, key) => [...acc, isEmpty(basementComponents[key])], [])
            .every((el) => el);
    }

    const handleCreateSystem = ({ type, manufacturer, modelId, favId }) => {
        dispatch(removeFormErrors(`${accessor}.heating.system`));

        if (favId) {
            //Favourite System
        } else if (manufacturer && modelId) {
            //EStar
            const eStarTemplate = getEStarOptions(type)[modelId] || {};

            const { [type]: { equipmentType: { id: eStarEquipId = 0 } = {} } = {} } = eStarTemplate;

            const { pilotLight = 0, flueDiameter = 0 } =
                type === "furnace"
                    ? getDefaultFurnaceSpecs(eStarEquipId) || {}
                    : type === "boiler"
                      ? getDefaultBoilerSpecs(eStarEquipId) || {}
                      : {};

            eStarTemplate[type].specifications.pilotLight = pilotLight;
            eStarTemplate[type].specifications.flueDiameter = flueDiameter;

            change(`${accessor}.heating.system`, eStarTemplate);

            //Clear EStar selections for next time
            change(`${accessor}.heating.selected.eStar.manufacturer`, false);
            change(`${accessor}.heating.selected.eStar.modelId`, false);
        } else if (type) {
            //Create standard system from template
            const heatingTemplate = getHeatingSystemTemplate(type) || {};

            change(`${accessor}.heating.system`, heatingTemplate);
            if (!isUpgrade && ["combo", "p9Combo"].includes(type)) {
                change("model.modelData.domesticHotWater.primarySystem", getHeatingSystemTemplate("comboDhw"));
                change("model.modelData.domesticHotWater.secondarySystem", {});
                dispatch(removeFormErrors("model.modelData.domesticHotWater.primarySystem"));
                dispatch(removeFormErrors("model.modelData.domesticHotWater.secondarySystem"));
            }
        }
    };

    const activeHeatingSystem = typeContent(currentPrimarySystem) || {};

    const systemTypes = [
        {
            value: "baseboards",
            label: "Baseboards",
        },
        {
            value: "furnace",
            label: "Furnace",
        },
        {
            value: "boiler",
            label: "Boiler",
        },
        {
            value: "combo",
            label: "Combo Heating/DHW",
            disabled: isUpgrade,
        },
        {
            value: "p9Combo",
            label: "Combo Heating/DHW (CSA P.9-11)",
            disabled: isUpgrade,
        },
    ];

    const handleQuickCreate = (type) => {
        handleCreateSystem({
            type,
            manufacturer: false,
            modelId: false,
            favId: false,
        });
        toggleOpen(false);
    };

    return (
        <div className={!isUpgrade && sharedClasses.section}>
            {!hidePrimaryHeating && (
                <>
                    <div className={classes.heading}>
                        <div>
                            <h3>{activeHeatingSystem.name}</h3>
                        </div>
                        <Button className={classes.createButton} onClick={() => toggleOpen(true)}>
                            Change Heating System
                        </Button>
                    </div>
                    <Drawer
                        open={open}
                        close={() => toggleOpen(false)}
                        preTitle="New System"
                        title="Change Heating System"
                        topBarColor="#0eb0a9"
                    >
                        <div>
                            <div style={{ marginBottom: "2rem" }}>
                                <h4 style={{ marginBottom: "1rem", fontSize: "1rem", fontWeight: 600 }}>
                                    Create Default System
                                </h4>
                                <p style={{ marginBottom: "1.5rem", fontSize: "0.875rem", color: "#666" }}>
                                    Choose a system type to create with default specifications. You can customize the
                                    specifications after creation.
                                </p>
                                <div
                                    style={{
                                        display: "grid",
                                        gridTemplateColumns: "repeat(2, 1fr)",
                                        gap: "1rem",
                                    }}
                                >
                                    {systemTypes
                                        .filter((systemType) => !systemType.disabled)
                                        .map((systemType) => (
                                            <Button
                                                key={systemType.value}
                                                type="hollow"
                                                onClick={() => handleQuickCreate(systemType.value)}
                                                disabled={!canBeEditedFromParametric}
                                                icon={AddIcon}
                                                style={{
                                                    textAlign: "center",
                                                    justifyContent: "center",
                                                }}
                                            >
                                                {systemType.label}
                                            </Button>
                                        ))}
                                </div>
                            </div>

                            <Accordion
                                heading="Optional: Import ENERGY STAR System"
                                initOpenState={false}
                                className={classes.energyStarAccordion}
                            >
                                <div>
                                    <p style={{ marginBottom: "1.5rem", fontSize: "0.875rem", color: "#666" }}>
                                        Import specifications from an ENERGY STAR certified system. This will replace
                                        the default specifications. Only applicable for furnace and boilers.
                                    </p>
                                    <InputRow>
                                        <Field
                                            component={Select}
                                            name={`${accessor}.heating.selected.type`}
                                            options={[
                                                {
                                                    value: false,
                                                    label: "None",
                                                },
                                                {
                                                    value: "furnace",
                                                    label: "Furnace",
                                                },
                                                {
                                                    value: "boiler",
                                                    label: "Boiler",
                                                },
                                            ]}
                                            label="System Type"
                                            placeholder="Choose System Type"
                                            validate={primaryHeatingValidation}
                                            info={'"Type 1" system in HOT2000'}
                                            onChange={() => {
                                                change(`${accessor}.heating.selected.eStar.manufacturer`, "");
                                                change(`${accessor}.heating.selected.eStar.modelId`, "");
                                            }}
                                            disabled={!canBeEditedFromParametric}
                                        />
                                    </InputRow>
                                    <InputRow>
                                        <Field
                                            component={Select}
                                            name={`${accessor}.heating.selected.eStar.manufacturer`}
                                            options={[
                                                {
                                                    value: false,
                                                    label: "None",
                                                },
                                                ...getEStarManufacturerOptions(primarySystemType),
                                            ]}
                                            label="ENERGY STAR Manufacturer"
                                            placeholder="Choose Manufacturer"
                                            fieldKey={"heatingEStarManufacturer"}
                                            disabled={
                                                !["furnace", "boiler"].includes(primarySystemType) ||
                                                !canBeEditedFromParametric
                                            }
                                            info={
                                                "Select a manufacturer of an ENERGY STAR heating system. Only applicable for furnace and boilers."
                                            }
                                            search={true}
                                        />
                                    </InputRow>
                                    <InputRow>
                                        <Field
                                            component={Select}
                                            name={`${accessor}.heating.selected.eStar.modelId`}
                                            options={[
                                                {
                                                    value: false,
                                                    label: "None",
                                                },
                                                ...getEStarModelOptions({
                                                    systemType: primarySystemType,
                                                    manufacturer: eStarManufacturerValue,
                                                }),
                                            ]}
                                            label="ENERGY STAR Model"
                                            placeholder="Choose Model"
                                            fieldKey={"heatingEStarModel"}
                                            disabled={
                                                !["furnace", "boiler"].includes(primarySystemType) ||
                                                !eStarManufacturerValue ||
                                                !canBeEditedFromParametric
                                            }
                                            info={
                                                "Select an ENERGY STAR certified model. Only applicable for furnace and boilers."
                                            }
                                            search={true}
                                        />
                                    </InputRow>
                                    <div
                                        className={classes.buttons}
                                        style={{
                                            marginTop: "1.5rem",
                                            display: "flex",
                                            flexDirection: "column",
                                            gap: "1.25rem",
                                        }}
                                    >
                                        <Button
                                            onClick={() => {
                                                handleCreateSystem({
                                                    type: primarySystemType,
                                                    manufacturer: eStarManufacturerValue,
                                                    modelId: eStarModelId,
                                                });
                                                toggleOpen(false);
                                            }}
                                            disabled={
                                                !primarySystemType ||
                                                !["furnace", "boiler"].includes(primarySystemType) ||
                                                !eStarManufacturerValue ||
                                                !eStarModelId ||
                                                !canBeEditedFromParametric
                                            }
                                        >
                                            Import ENERGY STAR System
                                        </Button>
                                    </div>
                                </div>
                            </Accordion>

                            <div
                                className={classes.buttons}
                                style={{ marginTop: "2rem", display: "flex", flexDirection: "column", gap: "1.25rem" }}
                            >
                                <Button large type="hollow" onClick={() => toggleOpen(false)}>
                                    Cancel
                                </Button>
                            </div>
                        </div>
                    </Drawer>
                    <activeHeatingSystem.Component
                        formName={formName}
                        accessor={`${accessor}.heating`}
                        change={change}
                        {...props}
                    />
                    <FansAndPumps formName={formName} accessor={`${accessor}.heating`} change={change} />
                </>
            )}
            {!hideSecondaryHeating && (
                <>
                    <div className={classes.title}>
                        <h4>Auxiliary Heating Systems</h4>
                        <AddSystem formName={formName} accessor={accessor} change={change} />
                    </div>
                    {isEmpty(supplSystems) && isEmpty(radiantSystem) && isEmpty(additionalOpenings) && (
                        <EmptyState title="No auxiliary heating systems to display" style={{}} />
                    )}
                    {!isEmpty(supplSystems) &&
                        Object.keys(supplSystems).map((id) => (
                            <SupplementaryHeating
                                formName={formName}
                                accessor={`${accessor}.supplSystems.systems.${id}`}
                                parentAccessor={accessor}
                                change={change}
                                key={id}
                                noBasementComponents={noBasementComponents}
                            />
                        ))}
                    {!isEmpty(radiantSystem) && (
                        <RadiantHeating
                            formName={formName}
                            accessor={`${accessor}.radiantHeating`}
                            parentAccessor={accessor}
                            change={change}
                        />
                    )}
                    {!isEmpty(additionalOpenings) && (
                        <AdditionalOpenings
                            formName={formName}
                            accessor={`${accessor}.additionalOpenings`}
                            parentAccessor={accessor}
                            change={change}
                        />
                    )}
                </>
            )}
        </div>
    );
};
